<template>
  <div class="fin-screen">
    <div class="fin-toolbar">
      <h2 class="fin-title">{{ $t("finance_tab_contract") }}</h2>
      <span class="fin-sub">{{ canEdit ? $t("finance_contract_admin_hint") : $t("finance_contract_readonly_hint") }}</span>
    </div>

    <finance-state v-if="status === 'loading'" kind="loading" :lines="3" />
    <finance-state v-else-if="status === 'error'" kind="error" icon="mdi-alert-circle-outline" :title="$t('finance_shell.error_title')" :text="$t('finance_contract_load_error')">
      <button type="button" class="ui-btn ui-btn--dark" @click="load"><v-icon size="16">mdi-refresh</v-icon>{{ $t("finance_shell.retry") }}</button>
    </finance-state>

    <template v-else>
      <section class="ui-card">
        <div class="ui-card__body fin-contract">
          <template v-if="contract">
            <div>
              <div class="fin-eyebrow">{{ $t("finance_contract_label") }}</div>
              <b class="fin-contract__no">{{ $t("finance_contract_number_short", { number: contract.contract_number }) }}</b>
              <span class="ui-muted">
                · {{ $t("finance_contract_valid") }} {{ localeDate(contract.valid_from) }} – {{ contract.valid_to ? localeDate(contract.valid_to) : $t("contract_no_end_date_short") }}
              </span>
              <div class="ui-muted">
                <template v-if="contract.contract_date">{{ $t("finance_contract_signed", { date: localeDate(contract.contract_date) }) }}</template>
                <template v-if="contract.contract_date && contract.document_url"> · </template>
                <a v-if="contract.document_url" :href="contract.document_url" target="_blank" rel="noopener">{{ $t("finance_contract_document_pdf") }}</a>
              </div>
            </div>
            <span class="fin-grow"></span>
            <span v-if="contractPill" class="ui-chip" :class="contractPill.tone === 'error' ? 'ui-chip--bad' : 'ui-chip--warn'">
              <v-icon size="14" color="currentColor">mdi-alert-outline</v-icon>{{ contractPill.text }}
            </span>
          </template>
          <p v-else class="ui-muted">
            {{ $t("finance_no_contract") }}
            <span v-if="shell.isAdmin" class="ui-chip ui-chip--bad">{{ $t("finance_missing") }}</span>
          </p>
        </div>
      </section>

      <div v-if="conflict" class="ui-banner ui-banner--info ui-banner--warn">
        <v-icon size="16" color="currentColor">mdi-alert-outline</v-icon>
        <span>{{ $t("finance_schedule_conflict") }}</span>
      </div>

      <section class="ui-card fin-table-card">
        <div class="ui-card__head"><b>{{ $t("finance_services_and_tariffs") }}</b></div>
        <div class="ui-table-wrap">
          <table class="ui-table">
            <thead>
              <tr>
                <th>{{ $t("finance_service_label") }}</th>
                <th>{{ $t("finance_tariff") }}</th>
                <th>{{ $t("price_valid_from") }}</th>
                <th>{{ $t("finance_payout_dynamics_long") }}</th>
                <th>{{ $t("finance_cash_model") }}</th>
                <th>{{ $t("finance_col_status") }}</th>
                <th v-if="canEdit"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!services.length"><td :colspan="canEdit ? 7 : 6" class="ui-muted fin-center">{{ $t("finance_no_services") }}</td></tr>
              <tr v-for="item in services" :key="item.service_id" class="is-click" :class="{ 'is-off': !item.is_active, 'is-on': item.service_id === selectedServiceId }" tabindex="0" @click="selectedServiceId = item.service_id" @keyup.enter="selectedServiceId = item.service_id">
                <td><b>{{ translate(item.name) }}</b></td>
                <td>
                  <template v-if="item.is_active && item.pricing">
                    {{ translate(tariffLabel(item.pricing)) }} · <b>{{ tariffValue(item.pricing) }}</b>
                    <v-tooltip v-if="item.pricing.is_verified === false" top max-width="320">
                      <template #activator="{ on, attrs }"><v-icon x-small color="warning" v-bind="attrs" v-on="on">mdi-alert-outline</v-icon></template>
                      <span>{{ $t("pricing_not_verified_desc") }}</span>
                    </v-tooltip>
                  </template>
                  <span v-else class="ui-muted">—</span>
                </td>
                <td><template v-if="item.is_active && item.pricing">{{ localeDate(item.pricing.valid_from) }}</template><span v-else class="ui-muted">—</span></td>
                <td>
                  <span v-if="!item.is_active" class="ui-muted">—</span>
                  <template v-else-if="scheduleOf(item)">
                    {{ scheduleOf(item) }}
                    <span v-if="differs(item)" class="ui-chip ui-chip--warn">{{ $t("finance_shell.contract.differs") }}</span>
                  </template>
                  <b v-else class="ui-warn">{{ $t("finance_schedule_not_agreed") }}</b>
                </td>
                <td>
                  <template v-if="item.is_active && item.uses_cash_flow_model && item.contract && item.contract.cash_flow_model">{{ cashModelLabel(item.contract.cash_flow_model) }}</template>
                  <span v-else class="ui-muted">—</span>
                </td>
                <td><span class="ui-chip ui-chip--pill" :class="item.is_active ? 'ui-chip--ok' : ''">{{ item.is_active ? $t("finance_status_active") : $t("finance_status_inactive") }}</span></td>
                <td v-if="canEdit" class="r fin-nowrap">
                  <template v-if="item.is_active">
                    <button type="button" class="ui-btn ui-btn--sm" @click.stop="openSetPrice(item)">{{ $t("change_price") }}</button>
                    <button v-if="item.pricing && item.pricing.is_verified === false" type="button" class="ui-btn ui-btn--sm ui-btn--accent" :disabled="verifyingId === item.pricing.id" @click.stop="verifyPrice(item.pricing)">{{ $t("confirm_price") }}</button>
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="fin-cardlist">
          <button v-for="item in services" :key="`c-${item.service_id}`" type="button" class="fin-tx" :class="item.is_active ? 'is-in' : 'is-cash'" @click="selectedServiceId = item.service_id">
            <span class="fin-tx__head">
              <b>{{ translate(item.name) }}</b>
              <span class="ui-chip ui-chip--pill" :class="item.is_active ? 'ui-chip--ok' : ''">{{ item.is_active ? $t("finance_status_active") : $t("finance_status_inactive") }}</span>
            </span>
            <span class="fin-tx__lines">
              <span v-if="item.is_active && item.pricing">{{ translate(tariffLabel(item.pricing)) }} <b>{{ tariffValue(item.pricing) }}</b></span>
              <span v-if="item.is_active && scheduleOf(item)">{{ scheduleOf(item) }}<span v-if="differs(item)" class="ui-chip ui-chip--warn">{{ $t("finance_shell.contract.differs") }}</span></span>
            </span>
            <span v-if="canEdit && item.is_active" class="fin-actions">
              <span class="ui-btn ui-btn--sm" role="button" tabindex="0" @click.stop="openSetPrice(item)" @keyup.enter.stop="openSetPrice(item)">{{ $t("change_price") }}</span>
            </span>
          </button>
        </div>
        <p class="fin-footnote fin-footnote--pad">{{ $t("finance_tariff_types_note") }}</p>
      </section>

      <div class="fin-two">
        <section class="ui-card">
          <div class="ui-card__head"><b>{{ $t("finance_price_history_title", { service: selectedService ? translate(selectedService.name) : "" }) }}</b></div>
          <div class="ui-card__body">
            <p v-if="!selectedHistory.length" class="ui-muted">{{ $t("finance_no_price_history") }}</p>
            <div v-for="entry in selectedHistory" :key="entry.id" class="fin-hist" :class="{ 'is-old': !entry.is_current }">
              <span>
                {{ localeDate(entry.valid_from) }} – {{ entry.valid_to ? localeDate(entry.valid_to) : "" }}
                <small>
                  <template v-if="entry.is_verified === false">{{ $t("finance_not_verified") }} · </template>
                  <template v-if="entry.created_by">{{ $t("finance_confirmed_by_name", { name: translate(entry.created_by) }) }}</template>
                  <template v-else>—</template>
                </small>
              </span>
              <b class="fin-num">{{ translate(entry.opis) }}</b>
            </div>
            <p class="fin-footnote">{{ $t("finance_price_history_note") }}</p>
          </div>
        </section>

        <section class="ui-card">
          <div class="ui-card__head"><b>{{ $t("finance_how_payout_title") }}</b></div>
          <div class="ui-card__body">
            <ol class="fin-steps">
              <li v-for="n in 4" :key="n"><span>{{ $t(`finance_how_payout_${n}`) }}</span></li>
            </ol>
            <button type="button" class="ui-link fin-link--block" @click="shell.openScreen('settlements')">{{ $t("finance_view_settlements") }} →</button>
          </div>
        </section>
      </div>
    </template>

    <set-price-dialog v-if="canEdit && priceService" v-model="priceOpen" :restaurant-id="shell.restaurantId" :service-id="priceService.service_id" :paying-types="payingTypes" :current-price="priceCurrent" @saved="load" />
  </div>
</template>

<script>
import SetPriceDialog from "@/components/contracts/SetPriceDialog.vue";
import contractsService from "@/services/contracts";
import pricingService from "@/services/pricing";
import restaurantFinanceService from "@/services/restaurantFinance";
import restaurantService from "@/services/restaurant";
import { CASH_FLOW_MODEL_LABEL_KEYS, PAYOUT_SCHEDULE_LABEL_KEYS, formatMoney } from "@/components/restaurant/finance/constants";
import FinanceState from "../parts/FinanceState.vue";

/**
 * Ugovor i cijene: ugovor i rok, usluge sa tarifom, dinamikom isplate i modelom keša, istorija cijena i kako se
 * računa isplata. Restoran gleda; administrator mijenja i potvrđuje cijenu kroz postojeći dijalog cjenovnika.
 * Usluge čija se dinamika razlikuje od one koju backend primjenjuje (settlement.schedule) označene su adminu.
 */
export default {
  name: "FinanceContract",
  components: { FinanceState, SetPriceDialog },
  inject: { shell: "reportShell" },
  props: { period: { type: Object, default: null } },
  data: () => ({
    status: "loading",
    contract: null,
    services: [],
    options: {},
    history: [],
    settlement: null,
    canEditServer: true,
    selectedServiceId: null,
    priceOpen: false,
    priceService: null,
    verifyingId: null,
  }),
  computed: {
    canEdit() {
      return this.shell.isAdmin && this.canEditServer;
    },
    conflict() {
      return Boolean(this.shell.isAdmin && this.settlement && this.settlement.conflict);
    },
    selectedService() {
      return this.services.find((s) => s.service_id === this.selectedServiceId) || null;
    },
    selectedHistory() {
      return this.history.filter((h) => h.service_id === this.selectedServiceId).sort((a, b) => String(b.valid_from).localeCompare(String(a.valid_from)));
    },
    payingTypes() {
      return Object.entries(this.options.paying_types || {}).map(([value, text]) => ({ value: Number(value), text }));
    },
    priceCurrent() {
      return this.history.find((h) => this.priceService && h.service_id === this.priceService.service_id && h.is_current) || null;
    },
    // Rok ugovora: istekao = greška ("i dalje se obračunava"), inače upozorenje sa brojem dana.
    contractPill() {
      const days = this.contract && this.contract.dana_do_isteka;
      if (days === null || days === undefined) return null;
      if (this.contract.status === "ISTEKAO" || days < 0) {
        return { tone: "error", text: `${this.$t("contract_expired_days_ago", { count: Math.abs(days) })} · ${this.$t("still_being_billed")}` };
      }
      return { tone: "warning", text: this.$t("finance_expires_in_days", { count: days }) };
    },
  },
  created() {
    this.load();
  },
  methods: {
    numberText(value) {
      return Number(value).toLocaleString("sr-RS", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    },
    // "Procenat od prodaje": naziv tipa tarife iz options.paying_types (ne ručno sastavljen)
    tariffLabel(pricing) {
      return (this.options.paying_types || {})[pricing.paying_type] || "";
    },
    tariffValue(pricing) {
      if (pricing.amount === null || pricing.amount === undefined) return pricing.opis || "—";
      return Number(pricing.paying_type) === 2 ? `${this.numberText(pricing.amount)} %` : formatMoney(pricing.amount, this.shell.currency);
    },
    scheduleOf(service) {
      const contract = service.contract;
      if (!contract || !contract.payout_schedule) return "";
      if (contract.payout_schedule === "THRESHOLD" && contract.payout_threshold_amount !== null && contract.payout_threshold_amount !== undefined) {
        return this.$t("finance_schedule_threshold_amount", { amount: formatMoney(contract.payout_threshold_amount, this.shell.currency) });
      }
      const key = PAYOUT_SCHEDULE_LABEL_KEYS[contract.payout_schedule];
      return key ? this.$t(key) : contract.payout_schedule;
    },
    differs(service) {
      return Boolean(this.conflict && service.is_active && service.contract && service.contract.payout_schedule && this.settlement.schedule && service.contract.payout_schedule !== this.settlement.schedule);
    },
    cashModelLabel(model) {
      const key = CASH_FLOW_MODEL_LABEL_KEYS[model];
      return key ? this.$t(key) : model;
    },
    openSetPrice(service) {
      this.priceService = service;
      this.$nextTick(() => {
        this.priceOpen = true;
      });
    },
    async verifyPrice(pricing) {
      this.verifyingId = pricing.id;
      try {
        await pricingService.verifyRestaurantPrice(this.shell.restaurantId, pricing.id);
        this.shell.showToast(this.$t("price_confirmed_successfully"));
        await this.load();
      } catch (error) {
        this.shell.showToast((error && error.response && error.response.data && error.response.data.message) || this.$t("finance_contract_load_error"));
      } finally {
        this.verifyingId = null;
      }
    },
    async load() {
      const id = this.shell.restaurantId;
      if (!id) return;
      if (!this.services.length) this.status = "loading";
      try {
        const [contractsRes, servicesRes, pricingRes, overviewRes] = await Promise.all([
          contractsService.getRestaurantContracts(id).catch(() => null),
          restaurantService.getRestaurantServices(id),
          pricingService.getRestaurantPricing(id).catch(() => null),
          this.shell.isAdmin ? restaurantFinanceService.getFinancialOverview(id).catch(() => null) : Promise.resolve(null),
        ]);
        // Prvi ugovor u listi je aktivan (backend sortira aktivan prvi, pa ostali unazad)
        this.contract = (contractsRes && contractsRes.data && contractsRes.data.contracts && contractsRes.data.contracts[0]) || null;
        const servicesData = (servicesRes.data && servicesRes.data.data) || servicesRes.data || {};
        this.services = servicesData.services || [];
        this.options = servicesData.options || {};
        this.canEditServer = servicesData.can_edit !== false;
        const pricingData = (pricingRes && ((pricingRes.data && pricingRes.data.data) || pricingRes.data)) || {};
        this.history = pricingData.history || [];
        if (!this.options.paying_types && pricingData.options) {
          this.options = { ...this.options, ...pricingData.options };
        }
        const overview = overviewRes && ((overviewRes.data && overviewRes.data.data) || overviewRes.data);
        this.settlement = (overview && overview.settlement) || null;
        const selectable = this.services.filter((s) => s.is_active);
        if (!this.selectedService || !this.selectedService.is_active) {
          this.selectedServiceId = selectable.length ? selectable[0].service_id : null;
        }
        this.status = "ready";
      } catch (error) {
        this.status = "error";
      }
    },
  },
};
</script>
